import { AIIconWrapper } from "@amzn/stencil-react-components/ai";
import { Card } from "@amzn/stencil-react-components/card";
import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import {
  ProgressTracker,
  ProgressTrackerLayout,
  ProgressTrackerStep,
  ProgressTrackerStepStatus,
} from "@amzn/stencil-react-components/progress-tracker";
import { Status, StatusIndicator } from "@amzn/stencil-react-components/status-indicator";
import { Text } from "@amzn/stencil-react-components/text";
import IconArrowLeftSmall from "@amzn/stencil-react-icons/icons/icon-arrow-left-small";
import IconArrowRightSmall from "@amzn/stencil-react-icons/icons/icon-arrow-right-small";
import IconChipSmall from "@amzn/stencil-react-icons/icons/icon-chip-small";
import IconCopySmall from "@amzn/stencil-react-icons/icons/icon-copy-small";
import IconLightbulbSmall from "@amzn/stencil-react-icons/icons/icon-lightbulb-small";
import IconSparklesSmall from "@amzn/stencil-react-icons/icons/icon-sparkles-small";
import Button, { ButtonVariant } from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import { trackUxmCustomEvent } from "common/helpers/analytics-helper";
import { getString } from "common/uistringlabels/uiStringUtils";
import React, { FC, useCallback, useState } from "react";

export interface UseCaseStep {
  title: string;
  expectedResult: string;
  codeSnippet?: string;
  prompt?: string;
  learning?: string;
}

const CopyButton: FC<{ text: string; contentType: "prompt" | "code"; useCaseId?: string; stepIndex?: number }> = ({
  text,
  contentType,
  useCaseId,
  stepIndex,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    trackUxmCustomEvent("usecase-copy-content", { useCaseId, stepIndex, contentType });
    setTimeout(() => setCopied(false), 2000);
  }, [text, useCaseId, stepIndex, contentType]);

  return (
    <Button
      variant={ButtonVariant.Tertiary}
      onClick={handleCopy}
      aria-label={getString("useCases.steps.copy")}
      touchpoint="usecase-copy-content"
      customData={{ contentType, stepIndex }}
    >
      <Row gridGap="dimensions.spacing.100" alignItems="center">
        <IconCopySmall aria-hidden="true" />
        <Text fontSize="T100">{copied ? getString("useCases.steps.copied") : getString("useCases.steps.copy")}</Text>
      </Row>
    </Button>
  );
};

const PRE_STYLE_BASE = {
  margin: 0,
  whiteSpace: "pre-wrap" as const,
  fontSize: "var(--st-dt-font-size-t100)",
  maxHeight: 200,
  overflowY: "auto" as const,
};

const StepContent: FC<{
  step: UseCaseStep;
  onPrev?: () => void;
  onNext?: () => void;
  useCaseId?: string;
  stepIndex?: number;
}> = ({ step, onPrev, onNext, useCaseId, stepIndex }) => (
  <Card padding="dimensions.spacing.400">
    <Col gridGap="dimensions.spacing.300" width="100%">
      <Text fontSize="T400" fontWeight="bold">
        {step.title}
      </Text>
      <StatusIndicator status={Status.Positive} messageText={step.expectedResult} />

      {step.prompt && (
        <Col gridGap="dimensions.spacing.100">
          <Row gridGap="dimensions.spacing.100" alignItems="center" justifyContent="space-between">
            <Row gridGap="dimensions.spacing.100" alignItems="center">
              <AIIconWrapper>
                <IconSparklesSmall aria-hidden="true" />
              </AIIconWrapper>
              <Text fontSize="T100" fontWeight="bold">
                {getString("useCases.steps.prompt")}
              </Text>
            </Row>
            <CopyButton text={step.prompt} contentType="prompt" useCaseId={useCaseId} stepIndex={stepIndex} />
          </Row>
          <View
            backgroundColor="color.neutral.90"
            padding="dimensions.spacing.300"
            borderRadius="dimensions.spacing.100"
          >
            <pre style={{ ...PRE_STYLE_BASE, color: "var(--st-dt-color-neutral-05)" }}>{step.prompt}</pre>
          </View>
        </Col>
      )}

      {step.codeSnippet && (
        <Col gridGap="dimensions.spacing.100">
          <Row gridGap="dimensions.spacing.100" alignItems="center" justifyContent="space-between">
            <Row gridGap="dimensions.spacing.100" alignItems="center">
              <IconChipSmall title={getString("useCases.steps.codeSnippet")} />
              <Text fontSize="T100" fontWeight="bold">
                {getString("useCases.steps.codeSnippet")}
              </Text>
            </Row>
            <CopyButton text={step.codeSnippet} contentType="code" useCaseId={useCaseId} stepIndex={stepIndex} />
          </Row>
          <View
            backgroundColor="color.neutral.90"
            padding="dimensions.spacing.300"
            borderRadius="dimensions.spacing.100"
          >
            <pre style={{ ...PRE_STYLE_BASE, color: "var(--st-dt-color-green-30)" }}>{step.codeSnippet}</pre>
          </View>
        </Col>
      )}

      {step.learning && (
        <Card backgroundColor="color.yellow.05" padding="dimensions.spacing.300">
          <Col gridGap="dimensions.spacing.100">
            <Row gridGap="dimensions.spacing.100" alignItems="center">
              <IconLightbulbSmall title={getString("useCases.steps.learning")} />
              <Text fontSize="T100" fontWeight="bold">
                {getString("useCases.steps.learning")}
              </Text>
            </Row>
            <Text fontSize="T200">{step.learning}</Text>
          </Col>
        </Card>
      )}

      {/* Step navigation */}
      <Row justifyContent="space-between" alignItems="center">
        <Button
          variant={ButtonVariant.Tertiary}
          disabled={!onPrev}
          onClick={onPrev}
          aria-label={getString("useCases.steps.previous")}
          touchpoint="usecase-step-prev"
          customData={{ stepIndex }}
        >
          <Row gridGap="dimensions.spacing.100" alignItems="center">
            <IconArrowLeftSmall aria-hidden="true" />
            <Text fontSize="T100">{getString("useCases.steps.previous")}</Text>
          </Row>
        </Button>
        <Button
          variant={ButtonVariant.Tertiary}
          disabled={!onNext}
          onClick={onNext}
          aria-label={getString("useCases.steps.next")}
          touchpoint="usecase-step-next"
          customData={{ stepIndex }}
        >
          <Row gridGap="dimensions.spacing.100" alignItems="center">
            <Text fontSize="T100">{getString("useCases.steps.next")}</Text>
            <IconArrowRightSmall aria-hidden="true" />
          </Row>
        </Button>
      </Row>
    </Col>
  </Card>
);

export interface UseCaseStepsProps {
  steps: UseCaseStep[];
  useCaseId?: string;
}

export const UseCaseSteps: FC<UseCaseStepsProps> = ({ steps, useCaseId }) => {
  const [selected, setSelected] = useState(0);
  const isFirst = selected === 0;
  const isLast = selected === steps.length - 1;

  const navigateStep = (index: number) => {
    setSelected(index);
    trackUxmCustomEvent("usecase-step-navigate", { useCaseId, stepIndex: index });
  };

  return (
    <Row gridGap="dimensions.spacing.400" alignItems="flex-start">
      <Col minWidth={260} maxWidth={300} gridGap="dimensions.spacing.200">
        <Text fontSize="T100" color="color.text.secondary" fontWeight="bold" fontStyle="italic">
          {getString("useCases.steps.clickToNavigate")}
        </Text>
        <ProgressTracker
          layout={ProgressTrackerLayout.Vertical}
          selectedStep={selected}
          onStepSelected={(i: number) => navigateStep(i)}
          aria-label={getString("useCases.steps.ariaLabel")}
        >
          {steps.map((step, i) => (
            <ProgressTrackerStep
              key={i}
              labelText={getString("useCases.steps.stepLabel", { number: String(i + 1) })}
              descriptionText={step.title}
              status={i < selected ? ProgressTrackerStepStatus.Complete : ProgressTrackerStepStatus.Available}
            />
          ))}
        </ProgressTracker>
      </Col>
      <Col flex={1}>
        <StepContent
          step={steps[selected]}
          onPrev={isFirst ? undefined : () => navigateStep(selected - 1)}
          onNext={isLast ? undefined : () => navigateStep(selected + 1)}
          useCaseId={useCaseId}
          stepIndex={selected}
        />
      </Col>
    </Row>
  );
};
